import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import styled from "styled-components";
import { cva, VariantProps } from "class-variance-authority";

const HoverCard = HoverCardPrimitive.Root;

const HoverCardTrigger = HoverCardPrimitive.Trigger;

export const HoverCardPortal = HoverCardPrimitive.Portal;

const HoverCardArrow = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Arrow>
>(({ ...props }, ref) => (
  <HoverCardPrimitive.Arrow
    ref={ref}
    asChild={true}
    {...props}
    width={16}
    height={8}
  >
    <svg viewBox="0 0 16 8" fill="none">
      <g>
        <path
          d="M14.5 0.34314L8.17157 6.67157L1.84314 0.343139L8.17157 -5.98529L14.5 0.34314Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          d="M8.87866 7.37866C8.48814 7.76919 7.85497 7.76919 7.46445 7.37866L1.13602 1.05023L1.84313 0.343124L8.17155 6.67155L14.5 0.343125L15.2071 1.05023L8.87866 7.37866Z"
          fill="black"
          fillOpacity="0.1"
        />
      </g>
      <path
        d="M13.8477 1L2.5 0.999999L1.5 -1.16689e-06L14.8477 0L13.8477 1Z"
        fill="white"
      />
    </svg>
  </HoverCardPrimitive.Arrow>
));

HoverCardArrow.displayName = HoverCardPrimitive.Arrow.displayName;

// TODO: we should use class names for these styles once we have Tailwind
const StyledHoverCardContent = styled(HoverCardPrimitive.Content)`
  &[data-side="top"] {
    transform-origin: bottom;
  }

  &[data-side="right"] {
    transform-origin: left;
  }

  &[data-side="bottom"] {
    transform-origin: top;
  }

  &[data-side="left"] {
    transform-origin: right;
  }
`;

const hoverCardVariants = cva(
  "animation-fade-in-fast z-20 bg-white rounded-2 px3 py2.5 depth-800",
  {
    variants: {
      size: {
        sm: "w-44",
        "max-sm": "max-w-44",
        md: "w-64",
        "max-md": "max-w-64",
        lg: "w-[512px]",
        "max-lg": "max-w-[512px]",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content> &
    VariantProps<typeof hoverCardVariants>
>(
  (
    {
      children,
      className,
      sideOffset = 4,
      collisionPadding = 10,
      size = "sm",
      ...props
    },
    ref,
  ) => (
    <StyledHoverCardContent
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={hoverCardVariants({ size, className })}
      {...props}
    >
      <HoverCardArrow />
      {children}
    </StyledHoverCardContent>
  ),
);

HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

// Composed component for rendering in Rails views
function ComposedHoverCard({
  trigger,
  triggerProps,
  content,
  contentProps,
  ...props
}: {
  content: string;
  trigger: string;
  contentProps?: React.ComponentPropsWithoutRef<typeof HoverCardContent>;
  triggerProps?: React.ComponentPropsWithoutRef<typeof HoverCardTrigger> & {
    as?: React.ElementType;
  };
} & React.ComponentPropsWithoutRef<typeof HoverCard>) {
  const TriggerTag = triggerProps?.as ?? "a";

  return (
    <HoverCard {...props}>
      <HoverCardTrigger {...triggerProps} asChild={true}>
        <TriggerTag dangerouslySetInnerHTML={{ __html: trigger }} />
      </HoverCardTrigger>

      <HoverCardContent {...contentProps}>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </HoverCardContent>
    </HoverCard>
  );
}

export { HoverCard, HoverCardTrigger, HoverCardContent, ComposedHoverCard };
